import isNil from 'lodash/isNil';
import moment from 'moment';
import { all, call, delay, put, putResolve, select, spawn, takeEvery } from 'typed-redux-saga';

import { iframeAPI, walletsAPI, withdrawalAPI } from '@/api';
import { ApiTransactionAPIModelStatusEnum as TransactionAPIModelStatusEnum } from '@/generated/api/fps/core';
import { goalReached, YMGoals } from '@/infrastructure/ym';
import * as auth from '@/model/auth/actions';
import { makeSelectAuth, makeSelectInitialData } from '@/model/auth/selectors';
import { TransactionId, validValue } from '@/model/common';
import * as global from '@/model/global/actions';
import { makeSelectIsFrameView, makeSelectLoadingState, makeSelectNotifications } from '@/model/global/selectors';
import { withGlobalLock } from '@/model/global/utils';
import { RootState } from '@/model/types';
import * as wallets from '@/model/wallets/actions';
import { makeSelectDefaultWallet, makeSelectWallets } from '@/model/wallets/selectors';
import { Wallet } from '@/model/wallets/types';
import createCache from '@/utils/cache';
import { ErrorWithCode } from '@/utils/errors';
import { readQueryParam } from '@/utils/http';
import { ActionWithoutPayload, ActionWithPayload, takeEveryTypesafe } from '@/utils/redux';

import * as actions from './actions';
import {
  makeSelectFormData,
  makeSelectPhone,
  makeSelectStep,
  makeSelectTiming,
  makeSelectTransferCached,
} from './selectors';
import { CachedWithdrawalData, NAMESPACE, State, Timing, WithdrawalForm, WithdrawalStep } from './types';
import { onWithdrawalErrorMessage } from './utils';

const selectAuth = makeSelectAuth();
const selectDefaultWallet = makeSelectDefaultWallet();
const selectFormData = makeSelectFormData();
const selectCachedData = makeSelectTransferCached();
const selectStep = makeSelectStep();
const selectTiming = makeSelectTiming();
const selectPhone = makeSelectPhone();
const selectLoading = makeSelectLoadingState();
const selectIsFrame = makeSelectIsFrameView();
const selectNotifications = makeSelectNotifications();

let pollInProgress = false;

// prettier-ignore
// prettier: FIXME: https://github.com/prettier/prettier/pull/7111
const checkIsFinalStatus = (status: TransactionAPIModelStatusEnum) =>
  status === TransactionAPIModelStatusEnum.Cancelled
  || status === TransactionAPIModelStatusEnum.Finished
  || status === TransactionAPIModelStatusEnum.Failed;

const cache = createCache<{ operationId?: TransactionId }>('MNX_WALLET_WITHDRAWAL');

const toFormData = (
  payload: actions.SaveWithdrawalFormDataActionPayload,
  formData: WithdrawalForm,
  state: State,
  allWallets: Wallet[],
): WithdrawalForm => {
  const wallet = !isNil(payload.wallet) ? payload.wallet : formData.wallet?.value;
  const amount = !isNil(payload.amount) ? payload.amount : formData.amount?.value;
  const bank = !isNil(payload.bank) ? payload.bank : formData.bank?.value;
  const message = !isNil(payload.message) ? payload.message : formData.message?.value;
  const walletBalance = wallet && allWallets.filter(({ id }) => id === wallet)[0]?.balance?.amount;
  return {
    ...formData,
    amount: {
      value: amount,
      hasError: !isNil(amount) && (amount < 0 || (!isNil(walletBalance) && amount > walletBalance)),
    },
    bank: validValue(bank),
    wallet: {
      value: wallet,
      hasError: !isNil(amount) && (amount < 0 || (!isNil(walletBalance) && amount > walletBalance)),
    },
    message: validValue(message),
  };
};

const isTimingExpired = (timing: Timing): boolean => moment(timing.expiresAt).isBefore();

function* waitTiming(timing: Timing): Generator {
  const tillNextOperation = moment(timing.nextAllowedAt)
    .add(1, 'seconds')
    .diff(new Date());
  if (tillNextOperation > 0) {
    yield delay(tillNextOperation);
  }
}

function* onAuthInit(): Generator {
  const { isNewUser, success } = yield* select(selectAuth);
  if (!success || isNewUser) {
    cache.clear();
  }
}

function* pollWithdrawalStatus(
  nspkTimeout: boolean,
  operationId: TransactionId,
  createdAt: Date,
  sbpId?: string,
): Generator {
  if (pollInProgress) {
    return;
  }
  pollInProgress = true;
  if (nspkTimeout) {
    const waitNSPKTimeout = moment(createdAt)
      .add(10, 'seconds')
      .diff(new Date());
    if (waitNSPKTimeout > 0) {
      if (sbpId) {
        yield* putResolve(
          actions.storeStep({
            step: WithdrawalStep.Running,
            transactionId: operationId,
            sbpId,
          }).pure,
        );
      }
      yield delay(waitNSPKTimeout);
    }
  }
  try {
    while (true) {
      const { status, ...tx } = yield* call(walletsAPI.queryTransaction, operationId);
      const { transactionId } = yield* select(selectStep);
      if (transactionId !== operationId) {
        // that should not happened
        return;
      }

      const storedWithdrawal = yield* select(selectFormData);
      if (storedWithdrawal && storedWithdrawal.createdAt?.getTime() !== tx.timestamp.getTime()) {
        yield put(
          actions.storeWithdrawal({
            ...storedWithdrawal,
            createdAt: tx.timestamp,
          }).pure,
        );
      }

      const isFinalStatus = checkIsFinalStatus(status);

      const isFrame = yield* select(selectIsFrame);
      if (status === TransactionAPIModelStatusEnum.Finished) {
        if (isFrame) {
          iframeAPI.sendWithdrawalFinished('SUCCESS');
        }
        goalReached(YMGoals.WITHDRAWAL_SUCCESS);
      } else if (
        // prettier-ignore
        // prettier: FIXME: https://github.com/prettier/prettier/pull/7111
        status === TransactionAPIModelStatusEnum.Failed
        || status === TransactionAPIModelStatusEnum.Cancelled
      ) {
        if (isFrame) {
          iframeAPI.sendWithdrawalFinished('FAILED');
        }
        goalReached(YMGoals.WITHDRAWAL_ERROR);
      }
      // refresh balance
      if (status === TransactionAPIModelStatusEnum.Finished) {
        yield putResolve(wallets.reloadBalance({ force: true }).pure);
      }
      yield* putResolve(
        actions.storeStep({
          // eslint-disable-next-line no-nested-ternary
          step: isFinalStatus
            ? WithdrawalStep.Finished
            : status === TransactionAPIModelStatusEnum.Initiated
            ? WithdrawalStep.Running
            : undefined,
          status,
          sbpId: tx.sbpInfo?.id,
          transactionId: operationId,
        }).pure,
      );
      if (isFinalStatus) {
        yield put(actions.notifyWithdrawalFinished().pure);
        cache.clear();
        break;
      } else {
        yield delay(5000);
      }
    }
  } catch (e) {
    yield* putResolve(
      actions.storeStep({
        step: WithdrawalStep.Finished,
        status: TransactionAPIModelStatusEnum.Unknown,
        transactionId: operationId,
      }).pure,
    );
  } finally {
    pollInProgress = false;
  }
}

function* init(): Generator {
  try {
    let state: actions.StoreInitActionPayload = {};

    const { operationId } = cache.read() || {};
    if (operationId) {
      try {
        const transaction = yield* call(walletsAPI.queryTransaction, operationId);
        if (transaction.sbpInfo?.id && transaction.transactionId && transaction.sbpInfo?.senderPhone) {
          const data: CachedWithdrawalData = {
            id: transaction.sbpInfo.id,
            fee: transaction.accountFromFee,
            receiver: {
              phone: transaction.sbpInfo.senderPhone,
              name: transaction.sbpInfo.senderInfo,
            },
            description: transaction.comment,
            createdAt: transaction.timestamp,
            amount: Math.abs(transaction.amount || 0),
            transactionId: transaction.transactionId,
            banks: [],
            accountId: transaction.account,
            bankId: transaction.sbpInfo.bankId,
          };
          state = {
            ...state,
            // eslint-disable-next-line no-nested-ternary
            step: checkIsFinalStatus(transaction.status)
              ? WithdrawalStep.Finished
              : transaction.status === TransactionAPIModelStatusEnum.Initiated
              ? WithdrawalStep.Running
              : undefined,
            data,
            status: transaction.status,
            sbpId: transaction.sbpInfo.id,
          };
          if (checkIsFinalStatus(transaction.status)) {
            cache.clear();
          }
        }
      } catch (e) {
        // do nothing
      }
    }
    yield* putResolve(actions.storeInitialized(state).pure);
    if (!state.data) {
      const shouldPresetPhone = readQueryParam('preset_phone') !== undefined;
      if (shouldPresetPhone) {
        const initData = yield* select(makeSelectInitialData());
        if (initData?.phone) {
          yield* putResolve(
            actions.savePhone({
              phone: initData.phone.number,
            }).pure,
          );
        }
      }
    }
    if (state.data?.transactionId && state.data.createdAt && state.sbpId) {
      yield* spawn(pollWithdrawalStatus, false, state.data.transactionId, state.data.createdAt, state.sbpId);
    }
  } finally {
    yield put(actions.notifyInitializationFinished().pure);
  }
}

function* logout(): Generator {
  cache.clear();
  yield* put(actions.storeLogout().pure);
}

function* savePhone({ payload: { phone } }: ActionWithPayload<actions.SavePhoneActionPayload>): Generator {
  yield* putResolve(
    actions.storePhone({
      value: phone,
      hasError: phone.length !== 10,
    }).pure,
  );
  return yield* select(selectFormData);
}

function* proceedToForm(): Generator {
  const phone = yield* select(selectPhone);
  if (!phone?.value) {
    // to make ts happier
    throw new ErrorWithCode('no-phone');
  }
  const savedData = yield* select(selectFormData);
  const oldTiming = yield* select(selectTiming);
  if (savedData?.receiver.phone === phone?.value && oldTiming && !isTimingExpired(oldTiming)) {
    // no need to refresh data if: 1. phone was not changed AND 2. timing was not expired or no timing
    return savedData;
  }

  const defaultWallet = yield* select(selectDefaultWallet);
  const { messageId, timing, banks, priorityBank } = yield* call(withdrawalAPI.discoverBanks, {
    phone: phone.value,
    walletId: savedData?.wallet?.value || defaultWallet?.id,
  });
  goalReached(YMGoals.WITHDRAWAL_PHONE_SET);
  const notifications = (yield* select(selectNotifications))
    .filter(({ type }) => type === NAMESPACE)
    .map((notification) => put(global.storeRemoveNotification(notification).pure));
  if (notifications) {
    yield all(notifications);
  }
  yield* putResolve(
    actions.storeWithdrawal({
      amount: savedData?.amount,
      receiverBanks: banks,
      id: messageId,
      bank:
        // previously selected bank (in exists in new bank list) or priority bank
        // eslint-disable-next-line no-nested-ternary
        savedData?.bank?.value && banks.map(({ id }) => id).includes(savedData?.bank?.value)
          ? savedData?.bank
          : priorityBank
          ? validValue(priorityBank)
          : undefined,
      wallet:
        // previously selected wallet or default wallet
        savedData?.wallet || !isNil(defaultWallet?.id) ? validValue(defaultWallet?.id) : undefined,
      receiver: {
        phone: phone.value,
        priorityBank,
      },
    }).pure,
  );
  yield* putResolve(actions.storeCache({}).pure);
  yield* putResolve(actions.storeTiming({ timing }).pure);
  yield* putResolve(actions.storeStep({ step: WithdrawalStep.Prepare }).pure);
  return yield* select(selectFormData);
}

function* saveWithdrawalFormData({
  payload,
}: ActionWithPayload<actions.SaveWithdrawalFormDataActionPayload>): Generator {
  const savedData = yield* select(selectFormData);
  if (!savedData) {
    throw new ErrorWithCode('no-form-data');
  }
  const withdrawalState = yield* select((state: RootState) => state.withdrawal);
  const walletsState = yield* select(makeSelectWallets());
  const newData: WithdrawalForm = toFormData(payload, savedData, withdrawalState, walletsState.all);
  const newWallet = newData.wallet?.value;
  if (newWallet && savedData.wallet?.value !== newData.wallet?.value) {
    const { loading } = yield* select(selectLoading);
    yield* putResolve(
      global.storeLoading({
        loading: true,
        messageId: 'withdrawal.messages.operations.prepare',
      }).pure,
    );
    try {
      const oldTiming = yield* select(selectTiming);
      if (oldTiming) {
        yield* waitTiming(oldTiming);
      }
      const { messageId, timing, banks, priorityBank } = yield* call(withdrawalAPI.discoverBanks, {
        phone: savedData.receiver.phone,
        walletId: newWallet,
      });
      yield* putResolve(
        actions.storeWithdrawal({
          ...savedData,
          receiverBanks: banks,
          id: messageId,
          bank:
            // previously selected bank (in exists in new bank list) or priority bank
            // eslint-disable-next-line no-nested-ternary
            savedData?.bank?.value && banks.map(({ id }) => id).includes(savedData?.bank?.value)
              ? savedData?.bank
              : priorityBank
              ? validValue(priorityBank)
              : undefined,
          receiver: {
            ...savedData.receiver,
            priorityBank,
          },
        }).pure,
      );
      yield* putResolve(actions.storeTiming({ timing }).pure);
    } finally {
      yield put(global.storeLoading({ loading }).pure);
    }
  } else {
    yield* putResolve(actions.storeWithdrawal(newData).pure);
  }
  return yield* select(selectFormData);
}

function* prepareTransfer(): Generator {
  let data = yield* select(selectFormData);
  if (!data?.id || !data.amount?.value || !data.bank?.value || !data.wallet?.value) {
    // to make ts happier
    throw new ErrorWithCode('no-required-form-data');
  }
  const cachedData = yield* select(selectCachedData);

  if (
    // prettier: FIXME: https://github.com/prettier/prettier/pull/7111
    // prettier-ignore
    data.bank.value === cachedData?.bank
    && data.amount.value === cachedData?.amount
    && data.wallet.value === cachedData?.wallet
    && data.message?.value === cachedData?.message
  ) {
    return data;
  }

  const { step } = yield* select(selectStep);
  if (step !== WithdrawalStep.Prepare) {
    const oldTiming = yield* select(selectTiming);
    if (oldTiming) {
      yield* waitTiming(oldTiming);
    }
    const { messageId, timing, banks, priorityBank } = yield* call(withdrawalAPI.discoverBanks, {
      phone: data.receiver.phone,
      walletId: data.wallet.value,
    });
    data = {
      ...data,
      receiverBanks: banks,
      id: messageId,
      bank: data?.bank?.value && banks.map(({ id }) => id).includes(data.bank.value) ? data.bank : undefined,
      receiver: {
        ...data.receiver,
        priorityBank,
      },
    };
    yield* putResolve(actions.storeTiming({ timing }).pure);
    yield* putResolve(actions.storeWithdrawal(data).pure);
  }
  const oldTiming = yield* select(selectTiming);
  if (oldTiming) {
    yield* waitTiming(oldTiming);
  }

  data = yield* select(selectFormData);
  if (!data?.id || !data.amount?.value || !data.bank?.value || !data.wallet?.value) {
    throw new ErrorWithCode('no-required-form-data');
  }
  const { messageId, timing, amount, fee } = yield* call(withdrawalAPI.prepareTransfer, {
    messageId: data.id,
    bankId: data.bank.value,
    amount: data.amount.value,
    description: data.message?.value,
  });
  goalReached(YMGoals.WITHDRAWAL_PREPARE);
  yield* putResolve(actions.storeTiming({ timing }).pure);

  yield* waitTiming(timing);
  const { name: receiverName } = yield* call(withdrawalAPI.getReceiverName, { messageId });

  data = {
    ...data,
    id: messageId,
    amount: validValue(amount),
    fee,
  };
  yield* putResolve(
    actions.storeCache({
      bank: data.bank?.value,
      amount,
      message: data.message?.value,
      wallet: data.wallet?.value,
    }).pure,
  );
  yield* putResolve(
    actions.storeWithdrawal({
      ...data,
      receiver: {
        ...data.receiver,
        name: receiverName,
      },
    }).pure,
  );
  yield* putResolve(actions.storeStep({ step: WithdrawalStep.Transfer }).pure);
  return data;
}

function* transfer(): Generator {
  const savedData = yield* select(selectFormData);
  if (!savedData?.id || !savedData.wallet?.value || !savedData.amount?.value) {
    // to make ts happier
    throw new ErrorWithCode('no-required-form-data');
  }
  const { operationId, messageId, sbpId } = yield* call(withdrawalAPI.transfer, {
    messageId: savedData.id,
    walletId: savedData.wallet.value,
    amount: savedData.amount.value,
  });
  goalReached(YMGoals.WITHDRAWAL_TRANSFER);

  cache.save({ operationId });
  yield* putResolve(
    actions.storeStep({
      step: WithdrawalStep.Running,
      transactionId: operationId,
      sbpId,
    }).pure,
  );
  const createdAt = new Date(); // we don't know the exactly time yet, this time should be update in a tx polling callback
  yield* putResolve(
    actions.storeWithdrawal({
      ...savedData,
      id: messageId,
      createdAt,
    }).pure,
  );
  yield* putResolve(actions.storeCache({}).pure);
  yield* putResolve(actions.storeTiming({}).pure);
  yield* spawn(pollWithdrawalStatus, true, operationId, createdAt, sbpId);
}

export const withTimingRespect = <P, T extends ActionWithPayload<P> | ActionWithoutPayload>(
  worker: (action: T) => Generator<unknown, void, T>,
): ((action: T) => Generator<unknown, void, T>) =>
  function* runner(action: T): Generator<unknown, void, T> {
    const timing = yield* select(selectTiming);
    if (timing) {
      if (isTimingExpired(timing)) {
        yield* putResolve(actions.storeTiming({}).pure);
        yield* put(
          global.addNotification({
            type: NAMESPACE,
            messageId: 'withdrawal.errors.operations.timing.expired',
            severity: 'error',
          }).pure,
        );
        goalReached(YMGoals.WITHDRAWAL_TIMEOUT);
        throw new ErrorWithCode('timing-expired');
      }
      yield* waitTiming(timing);
    }
    return yield* worker(action);
  };

export const withIgnoreTimingError = (onErrorWrapped: (error: unknown) => ErrorWithCode | Generator) =>
  function* onError(error: unknown): Generator {
    if ((error as ErrorWithCode).code === 'timing-expired') {
      return error;
    }
    return yield onErrorWrapped(error);
  };

export const withCleanStepOnError = (onErrorWrapped: (error: unknown) => ErrorWithCode | Generator) =>
  function* onError(error: unknown): Generator {
    const { step, ...stepData } = yield* select(selectStep);
    yield* putResolve(actions.storeStep(stepData).pure);
    return yield onErrorWrapped(error);
  };

export default function* rootSaga(): Generator {
  yield* takeEvery(
    auth.notifyAuthorizationFinished.type,
    withGlobalLock(onAuthInit, 'common.messages.operations.init'),
  );
  yield* takeEveryTypesafe(actions.init, withGlobalLock(init, 'common.messages.operations.init'));
  yield* takeEveryTypesafe(
    actions.saveWithdrawalFormData,
    saveWithdrawalFormData,
    withCleanStepOnError(onWithdrawalErrorMessage(NAMESPACE, 'withdrawal.errors.operations.prepare')),
  );
  yield* takeEveryTypesafe(actions.savePhone, withGlobalLock(savePhone, 'withdrawal.messages.operations.savePhone'));
  yield* takeEveryTypesafe(
    actions.proceedToForm,
    withGlobalLock(proceedToForm, 'withdrawal.messages.operations.proceedToForm'),
    withCleanStepOnError(onWithdrawalErrorMessage(NAMESPACE, 'withdrawal.errors.operations.proceedToForm')),
  );
  yield* takeEveryTypesafe(
    actions.prepare,
    withGlobalLock(withTimingRespect(prepareTransfer), 'withdrawal.messages.operations.prepare'),
    withCleanStepOnError(
      withIgnoreTimingError(onWithdrawalErrorMessage(NAMESPACE, 'withdrawal.errors.operations.prepare')),
    ),
  );
  yield* takeEveryTypesafe(
    actions.transfer,
    withGlobalLock(withTimingRespect(transfer), 'withdrawal.messages.operations.transfer'),
    withCleanStepOnError(
      withIgnoreTimingError(onWithdrawalErrorMessage(NAMESPACE, 'withdrawal.errors.operations.transfer')),
    ),
  );
  yield* takeEvery(global.notifyLogout.type, withGlobalLock(logout, 'common.messages.operations.logout'));
}
